// GraphQL Client for backend API
// In production, use relative path to leverage Vercel rewrites (avoids CORS)
// In development, use explicit URL
const getApiUrl = () => {
  // If VITE_API_URL is explicitly set, use it
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  
  // In production (on paytaya.com domains), use relative path
  // Vercel will proxy /graphql to the backend via vercel.json
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname.includes('1401.io') || hostname.includes('vercel.app')) {
      return '/graphql';
    }
  }
  
  // Default to localhost for development
  return 'http://localhost:3001/graphql';
};

const API_URL = getApiUrl();

// Token storage keys
const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_ROLE_KEY = 'user_role';
const USER_DATA_KEY = 'user_data';

class GraphQLClient {
  constructor() {
    this.apiUrl = API_URL;
  }

  // Token management
  setTokens(accessToken, refreshToken, role, userData) {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    if (role) localStorage.setItem(USER_ROLE_KEY, role);
    if (userData) localStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
  }

  getAccessToken() {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  }

  getRefreshToken() {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  getUserRole() {
    return localStorage.getItem(USER_ROLE_KEY);
  }

  getUserData() {
    const data = localStorage.getItem(USER_DATA_KEY);
    return data ? JSON.parse(data) : null;
  }

  clearTokens() {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_ROLE_KEY);
    localStorage.removeItem(USER_DATA_KEY);
  }

  // Make GraphQL request
  async request(query, variables = {}, skipAuth = false) {
    const headers = {
      'Content-Type': 'application/json',
    };

    if (!skipAuth) {
      const token = this.getAccessToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    try {
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({ query, variables }),
      });

      const result = await response.json();

      if (result.errors) {
        // Check if it's an authentication error
        const authError = result.errors.find(
          (err) => err.message?.includes('Unauthorized') || err.extensions?.code === 'UNAUTHENTICATED'
        );

        if (authError && !skipAuth) {
          // Try to refresh token
          const refreshed = await this.refreshAccessToken();
          if (refreshed) {
            // Retry the original request
            return this.request(query, variables, skipAuth);
          } else {
            // Refresh failed, logout
            this.clearTokens();
            throw new Error('Session expired. Please login again.');
          }
        }

        const errorMessage = result.errors[0].message || 'GraphQL Error';
        const errorExtensions = result.errors[0].extensions;
        const fullError = result.errors[0];
        
        // Try to extract a more detailed error message
        let detailedMessage = errorMessage;
        
        // Check multiple possible locations for the actual error message
        if (errorExtensions?.exception?.response?.message) {
          const msg = errorExtensions.exception.response.message;
          detailedMessage = Array.isArray(msg) ? msg.join(', ') : msg;
        } else if (errorExtensions?.response?.message) {
          const msg = errorExtensions.response.message;
          detailedMessage = Array.isArray(msg) ? msg.join(', ') : msg;
        } else if (errorExtensions?.originalError?.message) {
          detailedMessage = errorExtensions.originalError.message;
        }
        
        // Only log in development mode
        if (import.meta.env.DEV) {
          console.error('GraphQL Error Details:', {
            message: errorMessage,
            detailedMessage: detailedMessage,
            extensions: errorExtensions,
          });
        }
        
        // Create a custom error with permission info if it's a permission error
        const error = new Error(detailedMessage);
        error.isPermissionError = detailedMessage.includes('Insufficient permissions') || 
                                   detailedMessage.includes('permission') ||
                                   detailedMessage.toLowerCase().includes('forbidden');
        error.extensions = errorExtensions;
        
        throw error;
      }

      return result.data;
    } catch (error) {
      if (error.message === 'Session expired. Please login again.') {
        throw error;
      }
      
      // If it's already a permission error, preserve it
      if (error.isPermissionError) {
        throw error;
      }
      
      throw new Error(`Network error: ${error.message}`);
    }
  }

  // Refresh access token
  async refreshAccessToken() {
    const refreshToken = this.getRefreshToken();
    const role = this.getUserRole();
    
    if (!refreshToken || !role) {
      return false;
    }

    const mutationMap = {
      admin: 'adminRefreshToken',
      engine: 'engineRefreshToken',
      reseller: 'resellerRefreshToken',
      merchant: 'merchantRefreshToken',
    };

    const mutationName = mutationMap[role.toLowerCase()];
    if (!mutationName) {
      return false;
    }

    try {
      const query = `
        mutation RefreshToken($input: RefreshTokenInput!) {
          ${mutationName}(input: $input) {
            accessToken
            refreshToken
          }
        }
      `;

      const result = await this.request(
        query,
        { input: { refreshToken } },
        true // Skip auth for refresh request
      );

      if (result[mutationName]) {
        const { accessToken, refreshToken: newRefreshToken } = result[mutationName];
        this.setTokens(accessToken, newRefreshToken, role, this.getUserData());
        return true;
      }

      return false;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  }

  // Authentication methods
  async login(email, password, role) {
    const mutationMap = {
      admin: 'adminLogin',
      engine: 'engineLogin',
      reseller: 'resellerLogin',
      merchant: 'merchantLogin',
    };

    const mutationName = mutationMap[role.toLowerCase()];
    if (!mutationName) {
      throw new Error('Invalid role');
    }

    const query = `
      mutation Login($input: LoginInput!) {
        ${mutationName}(input: $input) {
          accessToken
          refreshToken
          userId
          email
          name
          role
        }
      }
    `;

    const result = await this.request(
      query,
      { input: { email, password } },
      true // Skip auth for login request
    );

    if (result[mutationName]) {
      const authData = result[mutationName];
      this.setTokens(
        authData.accessToken,
        authData.refreshToken,
        authData.role,
        {
          userId: authData.userId,
          email: authData.email,
          name: authData.name,
        }
      );
      return authData;
    }

    throw new Error('Login failed');
  }

  logout() {
    this.clearTokens();
  }

  isAuthenticated() {
    return !!this.getAccessToken();
  }

  // Get current user profile based on role
  async me() {
    const role = this.getUserRole();
    if (!role) {
      throw new Error('No authenticated user');
    }

    const queryMap = {
      admin: 'adminProfile',
      engine: 'engineProfile',
      reseller: 'resellerProfile',
      merchant: 'merchantProfile',
    };

    const queryName = queryMap[role.toLowerCase()];
    if (!queryName) {
      throw new Error('Invalid role');
    }

    const query = `
      query GetProfile {
        ${queryName} {
          id
          email
          name
          status
          createdAt
          updatedAt
        }
      }
    `;

    const result = await this.request(query);
    return result[queryName];
  }

  // Generic query method
  async query(queryString, variables = {}) {
    return this.request(queryString, variables);
  }

  // Generic mutation method
  async mutate(mutationString, variables = {}) {
    return this.request(mutationString, variables);
  }
}

// Export singleton instance
export const graphqlClient = new GraphQLClient();

// Export auth methods for easy access
export const auth = {
  login: (email, password, role) => graphqlClient.login(email, password, role),
  logout: () => graphqlClient.logout(),
  me: () => graphqlClient.me(),
  isAuthenticated: () => graphqlClient.isAuthenticated(),
  getUserRole: () => graphqlClient.getUserRole(),
  getUserData: () => graphqlClient.getUserData(),
};





